type ExternalIdValue = string | number | boolean | null | undefined;

type ExternalIdRecord = Record<string, ExternalIdValue>;

export type ExternalIds = ExternalIdRecord | Array<ExternalIdRecord> | null | undefined;

export interface ExternalIdProperty {
  name: string;
  value: string;
}

const toDisplayValue = (value: ExternalIdValue): string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return String(value);
};

const mapRecordToProperties = (record: ExternalIdRecord): Array<ExternalIdProperty> =>
  Object.entries(record)
    .map(([name, rawValue]) => {
      const value = toDisplayValue(rawValue);
      return value ? { name, value } : null;
    })
    .filter(Boolean) as Array<ExternalIdProperty>;

export const mapExternalIdsToProperties = (externalIds: ExternalIds): Array<ExternalIdProperty> => {
  if (!externalIds) {
    return [];
  }

  if (Array.isArray(externalIds)) {
    return externalIds.flatMap(mapRecordToProperties);
  }

  return mapRecordToProperties(externalIds);
};

export const getVisibleExternalIdKeys = (
  externalIds: Record<string, string> | undefined,
): Array<string> =>
  Object.entries(externalIds || {})
    .filter(([, value]) => value !== '' && value != null)
    .map(([key]) => key)
    .sort((a, b) => a.localeCompare(b));

export const getLocalOnlyExternalIdKeys = (
  formExternalIds: Record<string, string> | undefined,
  savedExternalIds: ExternalIds,
): Set<string> => {
  const savedKeys = new Set(Object.keys(normalizeExternalIdsForForm(savedExternalIds)));

  return new Set(
    getVisibleExternalIdKeys(formExternalIds).filter(key => !savedKeys.has(key)),
  );
};

export const normalizeExternalIdsForForm = (externalIds: ExternalIds): Record<string, string> => {
  if (!externalIds) {
    return {};
  }

  if (Array.isArray(externalIds)) {
    return externalIds.reduce<Record<string, string>>((acc, record) => {
      Object.entries(record).forEach(([key, rawValue]) => {
        const value = toDisplayValue(rawValue);
        if (value) {
          acc[key] = value;
        }
      });
      return acc;
    }, {});
  }

  return Object.entries(externalIds).reduce<Record<string, string>>((acc, [key, rawValue]) => {
    const value = toDisplayValue(rawValue);
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const formatExternalIdsForPayload = (
  formExternalIds: Record<string, string> | undefined,
  originalExternalIds: ExternalIds | undefined,
): ExternalIds | undefined => {
  if (!formExternalIds) {
    return undefined;
  }

  const payload = Object.fromEntries(
    Object.entries(formExternalIds).map(([key, value]) => [key, value ?? '']),
  );

  if (Object.keys(payload).length === 0) {
    return Array.isArray(originalExternalIds) ? [] : {};
  }

  if (Array.isArray(originalExternalIds)) {
    return Object.entries(payload).map(([key, value]) => ({ [key]: value }));
  }

  return payload;
};
